import React from "react";
import { RouterProvider } from "react-router-dom";
import { Provider } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { SettingsProvider } from "@app/layout/settings";
import MuiThemeProvider from "@app/theme/MuiThemeProvider";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "@app/style/global.scss";
import "simplebar-react/dist/simplebar.min.css";
import "react-lazy-load-image-component/src/effects/blur.css";
import { AppProvider } from "@app/hooks/useApp";
import SnackbarProvider from "@app/components/snackbar";
import store from "@app/redux/store";
import AppRoutes from "./App";
import reportWebVitals from "./reportWebVitals";

// Some third-party UI libs can trigger a benign Chromium warning that CRA treats
// as a runtime error overlay. We suppress only this specific message.
const RESIZE_OBSERVER_NOISE_RE =
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i;

if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    if (RESIZE_OBSERVER_NOISE_RE.test(event?.message ?? "")) {
      event.stopImmediatePropagation();
    }
  });

  // eslint-disable-next-line no-console
  const originalConsoleError = console.error.bind(console);
  // eslint-disable-next-line no-console
  console.error = (...args: unknown[]) => {
    const first = args[0];
    if (typeof first === "string" && RESIZE_OBSERVER_NOISE_RE.test(first)) return;
    originalConsoleError(...args);
  };
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  // <React.StrictMode>
  <Provider store={store}>
    <HelmetProvider>
      <SettingsProvider
        defaultSettings={{
          themeMode: "light", // 'light' | 'dark'
          themeDirection: "ltr", //  'rtl' | 'ltr'
          themeContrast: "default", // 'default' | 'bold'
          themeLayout: "vertical", // 'vertical' | 'horizontal' | 'mini'
          themeColorPresets: "purple", // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
          themeStretch: false,
        }}
      >
        <MuiThemeProvider>
          <AppProvider>
            <SnackbarProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <RouterProvider router={AppRoutes} />
              </LocalizationProvider>
            </SnackbarProvider>
          </AppProvider>
        </MuiThemeProvider>
      </SettingsProvider>
    </HelmetProvider>
  </Provider>
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
